import React, { useState, useRef } from 'react';

import { Helmet } from 'react-helmet';
import SEOHelmet from '../../components/SEOHelmet';
import { motion, AnimatePresence } from 'framer-motion';

import PublicNavigation from '../../components/ui/PublicNavigation';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ContactSection from '../shipping-information/components/ContactSection';
import QuoteCalculator from '../shipping-information/components/QuoteCalculator';
import { supabase } from '../../lib/supabase';
import { Link } from 'react-router-dom';


const DedicatedPricingPage = () => {
  const [selectedZone, setSelectedZone] = useState('abidjan');
  const [activeTab, setActiveTab] = useState('cartons');
  const [openFaq, setOpenFaq] = useState(null);

  // Refs for scroll targets
  const calculatorRef = useRef(null);
  const catalogRef = useRef(null);

  // Scroll handlers
  const scrollToCalculator = () => {
    calculatorRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const scrollToCatalog = () => {
    catalogRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // PDF Download handler - Opens prospectus from Supabase storage
  const handleDownloadPDF = () => {
    // Direct link to prospectus JPEG in Supabase storage
    const prospectusUrl = 'https://hknheuezaamvtltwcphf.supabase.co/storage/v1/object/public/marketing-materials/prospectus-adt-2026.jpeg';
    window.open(prospectusUrl, '_blank');
  };

  const structuredDataPricing = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Tarifs envoi Côte d'Ivoire",
        "provider": {
          "@type": "Organization",
          "name": "ADT Express"
        },
        "offers": [
          {
            "@type": "Offer",
            "name": "Barrique noire 270L – Offre Été 2026",
            "price": "160",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock"
          },
          {
            "@type": "Offer",
            "name": "Barrique bleue 220L",
            "price": "180",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock"
          }
        ]
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Accueil",
            "item": "https://www.adtexpress.fr"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Tarifs",
            "item": "https://www.adtexpress.fr/tarifs"
          }
        ]
      }
    ]
  };

  // Offre spéciale barrique noire
  const specialOffer = {
    title: "BARRIQUE 270L NOIRE OFFERTE",
    subtitle: "TRANSPORT COMPLET 160€ TTC",
    description: "(paiement 60/40 - collecte domicile, livraison Abidjan)",
    promo: "Offre Été 2026",
    endDate: "septembre 2026",
    capacity: "270 litres",
    price: 160,
    features: [
      "Barrique neuve offerte",
      "Collecte à domicile Paris/Banlieue",
      "Transport maritime sécurisé",
      "Livraison à Abidjan incluse",
      "Paiement flexible 60/40"
    ]
  };

  // Catalogue complet des prix
  const pricingCatalog = {
    cartons: {
      label: "Cartons",
      icon: "Package",
      items: [
        { name: "Petit", dimensions: "60x40x40 cm", price: 90 },
        { name: "Moyen", dimensions: "80x50x50 cm", price: 120 },
        { name: "Grand", dimensions: "100x60x60 cm", price: 150 }
      ]
    },
    electromenager: {
      label: "Électroménager",
      icon: "Refrigerator",
      items: [
        { name: "Réfrigérateur Petit", price: 100 },
        { name: "Moyen", price: 150 },
        { name: "Grand", price: 150 },
        { name: "Très Grand", price: 250 }
      ]
    },
    televiseurs: {
      label: "Téléviseurs",
      icon: "Tv",
      items: [
        { name: "55 pouces", price: 300 },
        { name: "82 pouces", price: 350 },
        { name: "127 pouces", price: 450 }
      ]
    },
    meubles: {
      label: "Meubles",
      icon: "Armchair",
      items: [
        { name: "Canapé clic-clac", price: 350 },
        { name: "Salon cuir 3 pièces", price: 650 },
        { name: "Fauteuil", price: 350 },
        { name: "3 places", price: 300 },
        { name: "1 places", price: 200 },
        { name: "Canapé d\'angle", price: 600 },
        { name: "Lit Complet", price: "200-400" },
        { name: "Chambre", price: 200 },
        { name: "Salle à manger", price: 360 }
      ]
    },
    bagages: {
      label: "Bagages",
      icon: "Luggage",
      items: [
        { name: "Sac Voyage", price: 350 },
        { name: "Petit", price: 140 },
        { name: "Moyen 3 pièces", price: 180 },
        { name: "Grand 15 pièce", price: 150 },
        { name: "Très Grand", price: 180 }
      ]
    },
    matelas: {
      label: "Matelas & Divers",
      icon: "BedDouble",
      items: [
        { name: "Velo Enfant", price: 80 },
        { name: "Adulte", price: 90 },
        { name: "Poussette", price: 80 },
        { name: "Tapis", price: 120 }
      ]
    }
  };

  const barrelPricing = [
    {
      type: "Barrique noire",
      icon: "Package",
      color: "from-gray-700 to-gray-900",
      emptyPrice: {
        paris: 30,
        banlieue: 35
      },
      transportPrice: 150,
      totalParis: 180,
      totalBanlieue: 185,
      features: [
        "Capacité standard 270L",
        "Idéale pour affaires personnelles",
        "Le choix le plus économique",
        "Collecte à Paris ou banlieue"
      ],
      popular: true
    },
    {
      type: "Barrique bleue",
      icon: "Box",
      color: "from-blue-500 to-blue-700",
      emptyPrice: {
        paris: 40,
        banlieue: 45
      },
      transportPrice: 140,
      totalParis: 180,
      totalBanlieue: 185,
      features: [
        "Plus résistante",
        "Idéale pour envois volumineux",
        "Transport hors promo : 140€",
        "Capacité optimisée"
      ],
      popular: false
    }
  ];

  const cartonPricing = {
    type: "Cartons triple cannelure",
    icon: "PackageCheck",
    description: "Cartons robustes et sécurisés spécialement conçus pour le transport maritime",
    features: [
      "Résistance maximale au transport maritime",
      "Plusieurs tailles disponibles",
      "Tarification au volume",
      "Idéal pour objets fragiles"
    ],
    note: "Tarif sur mesure selon le volume et la destination"
  };

  const comparisonFeatures = [
    { feature: "Prix barrique vide (Paris)", noir: "30€", bleu: "40€" },
    { feature: "Prix barrique vide (Banlieue)", noir: "35€", bleu: "45€" },
    { feature: "Transport maritime vers Abidjan (hors promo)", noir: "150€", bleu: "140€" },
    { feature: "Promo Été 2026 tout compris", noir: "160€ TTC (barrique offerte)", bleu: "Non incluse" },
    { feature: "Résistance", noir: "Standard", bleu: "Renforcée" },
    { feature: "Capacité", noir: "270L", bleu: "220L" },
    { feature: "Idéal pour", noir: "Affaires personnelles", bleu: "Envois volumineux" }
  ];

  const faqPricing = [
    {
      question: "Y a-t-il des promotions sur les tarifs d'envoi vers la Côte d'Ivoire ?",
      answer: "Oui ! Notre Offre Été 2026 propose la barrique noire 270L offerte + transport complet France → Abidjan à 160€ TTC tout compris. Cette offre est valable jusqu'en septembre 2026."
    },
    {
      question: "Le prix inclut-il la livraison à Abidjan ?",
      answer: "Oui, le tarif affiché couvre l'acheminement complet par conteneur maritime de Paris jusqu'à Abidjan, incluant la livraison à domicile en Côte d'Ivoire. Aucun frais caché."
    },
    {
      question: "Puis-je envoyer plusieurs barriques ?",
      answer: "Bien sûr ! Vous pouvez envoyer autant de barriques que nécessaire. Pour les envois multiples, contactez-nous pour un devis personnalisé. Nous proposons des tarifs dégressifs pour les gros volumes."
    },
    {
      question: "Quelle barrique choisir pour le meilleur rapport qualité-prix ?",
      answer: "Avec l'Offre Été 2026, la barrique noire est le meilleur choix : 160€ TTC tout compris avec la barrique offerte. Hors promo, la barrique noire coûte 30€ (Paris) ou 35€ (banlieue) + 150€ de transport. La barrique bleue coûte 40€ (Paris) ou 45€ (banlieue) + 140€ de transport."
    },
    {
      question: "Y a-t-il des frais supplémentaires à prévoir ?",
      answer: "Les tarifs affichés incluent la barrique vide, le transport maritime, et la livraison à Abidjan. Les seuls frais supplémentaires éventuels sont les taxes douanières en Côte d'Ivoire, qui dépendent du contenu de votre envoi. Nous vous informons de ces frais avant le paiement."
    },
    {
      question: "Comment sont calculés les tarifs pour les cartons ?",
      answer: "Les cartons triple cannelure sont facturés selon le volume (en m³) et la destination. Contactez-nous avec les dimensions de vos cartons pour obtenir un devis précis et personnalisé."
    }
  ];

  const savingsTips = [
    {
      icon: "TrendingDown",
      title: "Profitez des promotions",
      description: "Suivez nos réseaux sociaux pour bénéficier de réductions régulières sur le transport maritime"
    },
    {
      icon: "Users",
      title: "Envois groupés",
      description: "Regroupez vos envois avec famille ou amis pour optimiser les coûts"
    },
    {
      icon: "Calendar",
      title: "Planifiez à l'avance",
      description: "Réservez votre place dans le prochain conteneur pour garantir le meilleur tarif"
    },
    {
      icon: "Package",
      title: "Optimisez le remplissage",
      description: "Remplissez au maximum votre barrique pour rentabiliser votre envoi"
    }
  ];

  return (
    <>
      <SEOHelmet
        title="Tarifs Envoi Côte d'Ivoire | Prix Barrique Abidjan | Offre Été 2026 160€ TTC - ADT Express"
        description="Tarifs transparents envoi Côte d'Ivoire : Offre Été 2026 barrique noire 270L offerte + transport 160€ TTC. Prix barrique noire hors promo 30€ Paris / 35€ banlieue + transport 150€. Barrique bleue 40€ Paris / 45€ banlieue + transport 140€."
        keywords={"tarif envoi Côte d'Ivoire, prix barrique Abidjan, coût conteneur France Côte d'Ivoire, tarif fret maritime Paris Abidjan, prix transport maritime Abidjan, coût barrique diaspora ivoirienne, envoyer barrique Côte d\u2019Ivoire, groupage maritime, tarif envoi Abidjan"}
        canonicalUrl="https://www.adtexpress.fr/tarifs"
        structuredData={structuredDataPricing}
      />
      <Helmet>
        <title>Tarifs envoi Côte d'Ivoire — Prix barriques et transport maritime | ADT Express</title>
        <meta name="description" content="Découvrez les tarifs ADT Express : Offre Été 2026 barrique noire 270L offerte + transport 160€ TTC. Barrique noire hors promo 30€ Paris / 35€ banlieue + 150€ transport. Barrique bleue 40€ Paris / 45€ banlieue + 140€ transport." />
        <meta name="keywords" content="tarif envoi Côte d'Ivoire, prix barrique Abidjan, coût transport maritime Côte d'Ivoire, tarif barrique noire, prix barrique bleue, achat barrique vide, envoi colis Abidjan prix" />
        <link rel="canonical" href="https://www.adtexpress.fr/tarifs" />
        <meta property="og:title" content="Tarifs envoi Côte d'Ivoire — Prix des barriques ADT Express" />
        <meta property="og:description" content="Prix transparents pour l'envoi de barriques vers Abidjan. Offre Été 2026 : 160€ TTC tout compris avec barrique noire offerte." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(structuredDataPricing)}
        </script>
      </Helmet>
      <div className="min-h-screen bg-white">
        <PublicNavigation />

        {/* Hero Section - Maritime Background */}
        <section className="relative bg-gradient-to-br from-green-600 to-green-800 text-white py-20 px-4 overflow-hidden pt-44">
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?q=80&w=2070&auto=format&fit=crop')"
            }}
          />
          
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/60" />
          
          {/* Content */}
          <div className="relative z-10 container mx-auto max-w-6xl h-full flex flex-col justify-center px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center text-white"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Tarifs envoi colis et barriques Côte d'Ivoire – Départ France
              </h1>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-6">
                Prix transport maritime Paris – Abidjan en groupage sécurisé
              </h2>
              <p className="text-lg md:text-xl text-gray-100 max-w-4xl mx-auto mb-8">
                Découvrez nos tarifs transparents pour l'envoi de colis, barriques et marchandises vers la Côte d'Ivoire. Nos expéditions en groupage maritime depuis la France permettent de réduire les coûts tout en garantissant un transport sécurisé jusqu'au port d'Abidjan.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  onClick={scrollToCalculator}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold shadow-lg"
                >
                  Calculer mon devis
                </Button>
                <Button 
                  size="lg" 
                  onClick={scrollToCatalog}
                  variant="outline" 
                  className="border-2 border-white bg-white !text-green-700 hover:bg-green-50 font-semibold shadow-lg"
                >
                  Voir les tarifs
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Professional Banner - Premium Version */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="w-full bg-gradient-to-r from-gray-50 to-stone-50 py-6 md:py-8"
        >
          <div className="max-w-6xl mx-auto px-6 md:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Text Content */}
              <div className="flex-1">
                <h3 className="text-lg md:text-xl font-serif text-gray-900 mb-2">
                  Professionnels & importateurs : accédez à nos conditions tarifaires dédiées.
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Tarification sur volume, conteneurs dédiés, accompagnement personnalisé.
                </p>
              </div>

              {/* CTA Button */}
              <div className="flex-shrink-0">
                <a href="/professional-services-landing-page">
                  <Button
                    variant="outline"
                    className="border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400 font-medium px-6 py-2.5 flex items-center whitespace-nowrap transition-all"
                  >
                    Découvrir l'offre professionnelle
                    <Icon name="ArrowRight" className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Offre Spéciale - Clean White with Green Border */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-5xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden border-l-8 border-green-600"
            >
              {/* Orange PROMO Badge */}
              <div className="relative">
                <div className="absolute top-6 left-6 bg-orange-500 text-white px-4 py-2 rounded-lg font-bold text-sm shadow-md">
                  PROMO
                </div>
                
                <div className="p-8 md:p-12 pt-20">
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                        {specialOffer?.title}
                      </h2>
                      <p className="text-2xl font-bold text-green-600 mb-2">
                        {specialOffer?.subtitle}
                      </p>
                      <p className="text-gray-600 mb-4">{specialOffer?.description}</p>
                      
                      {/* Countdown/End Date */}
                      <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6">
                        <p className="text-orange-900 font-semibold flex items-center">
                          <Icon name="Clock" className="mr-2 w-5 h-5" />
                          Offre valable jusqu'au {specialOffer?.endDate}
                        </p>
                      </div>
                      
                      <ul className="space-y-3 mb-6">
                        {specialOffer?.features?.map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <Icon name="CheckCircle" className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700 font-medium">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-8 text-center">
                      <Icon name="Package" className="w-24 h-24 mx-auto mb-4 text-green-600" />
                      <div className="mb-6">
                        <div className="text-gray-700 font-semibold mb-2">Capacité</div>
                        <div className="text-3xl font-bold text-green-600 mb-4">{specialOffer?.capacity}</div>
                        <div className="text-gray-700 font-semibold mb-2">Prix tout compris</div>
                        <div className="text-5xl font-bold text-green-600">{specialOffer?.price}€</div>
                      </div>
                      <a href="https://wa.me/33651988621" target="_blank" rel="noopener noreferrer">
                        <Button size="lg" className="w-full bg-green-600 hover:bg-green-700 text-white font-bold">
                          <Icon name="MessageCircle" className="mr-2" />
                          Profiter de l'offre
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Barrel Pricing Cards - Harmonized Design */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nos tarifs de barriques vers Abidjan
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Choisissez la formule adaptée à vos besoins et votre budget
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
              {barrelPricing?.map((barrel, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className="relative bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  {barrel?.popular && (
                    <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-1.5 rounded-full text-sm font-semibold z-10">
                      Le plus populaire
                    </div>
                  )}
                  
                  <div className={`bg-gradient-to-br ${barrel?.color} text-white p-8`}>
                    <div className="flex items-center mb-4">
                      <Icon name={barrel?.icon} className="w-12 h-12 mr-4" />
                      <h3 className="text-2xl font-bold">{barrel?.type}</h3>
                    </div>
                  </div>

                  <div className="p-8">
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-200">
                        <span className="text-gray-600">Barrique vide — Paris intra-muros</span>
                        <span className="font-bold text-lg text-gray-900">{barrel?.emptyPrice?.paris}€</span>
                      </div>
                      <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-200">
                        <span className="text-gray-600">Barrique vide — Banlieue</span>
                        <span className="font-bold text-lg text-gray-900">{barrel?.emptyPrice?.banlieue}€</span>
                      </div>
                      <div className="flex justify-between items-center mb-3 pb-3 border-b-2 border-green-500">
                        <span className="font-bold text-gray-900">Transport vers Abidjan (remplie)</span>
                        <span className="font-bold text-lg text-gray-900">{barrel?.transportPrice}€</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-900">Total Paris intra-muros</span>
                        <span className="font-bold text-2xl text-green-600">{barrel?.totalParis}€</span>
                      </div>
                    </div>

                    {/* SEO Micro-text */}
                    <div className="bg-green-50 rounded-lg p-4 mb-6">
                      <p className="text-sm text-green-900 leading-relaxed">
                        {barrel?.type === "Barrique noire" ? "Idéal pour les envois personnels vers Abidjan. Transport maritime sécurisé avec suivi en ligne." : "Solution économique pour expédier vos colis vers la Côte d'Ivoire en groupage maritime."}
                      </p>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {barrel?.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <Icon name="Check" className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <a href="https://wa.me/33651988621" target="_blank" rel="noopener noreferrer">
                      <Button 
                        className={`w-full font-semibold ${
                          barrel?.popular 
                            ? 'bg-green-600 hover:bg-green-700 text-white' :'border-2 border-green-600 !text-green-700 hover:bg-green-50 bg-white'
                        }`}
                      >
                        <Icon name="MessageCircle" className="mr-2" />
                        Commander cette barrique
                      </Button>
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Reassurance Line */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto mb-12"
            >
              <div className="bg-green-50 border-2 border-green-600 rounded-xl p-6">
                <div className="flex flex-wrap justify-center items-center gap-6 text-green-900">
                  <div className="flex items-center">
                    <Icon name="CheckCircle" className="w-5 h-5 text-green-600 mr-2" />
                    <span className="font-semibold">Départ 2 fois par mois</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="CheckCircle" className="w-5 h-5 text-green-600 mr-2" />
                    <span className="font-semibold">Livraison au port d'Abidjan</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="CheckCircle" className="w-5 h-5 text-green-600 mr-2" />
                    <span className="font-semibold">Suivi en ligne en temps réel</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="CheckCircle" className="w-5 h-5 text-green-600 mr-2" />
                    <span className="font-semibold">Assistance France & Côte d'Ivoire</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Bottom CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-3xl mx-auto"
            >
              <p className="text-xl text-gray-700 mb-4">
                Besoin d'un devis pour un envoi vers la Côte d'Ivoire ? Contactez-nous dès maintenant.
              </p>
              <a href="https://wa.me/33651988621" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white font-semibold">
                  <Icon name="MessageCircle" className="mr-2" />
                  Demander un devis
                </Button>
              </a>
            </motion.div>
          </div>
        </section>

        {/* Reassurance Block - 4 Icon Elements */}
        <section className="py-12 bg-gradient-to-br from-green-50 to-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-6xl mx-auto"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* +2 500 colis expédiés */}
                <div className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Package" className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="text-3xl font-bold text-green-600 mb-2">+2 500</div>
                  <div className="text-gray-700 font-medium">colis expédiés</div>
                </div>

                {/* 98% clients satisfaits */}
                <div className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="ThumbsUp" className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="text-3xl font-bold text-green-600 mb-2">98%</div>
                  <div className="text-gray-700 font-medium">clients satisfaits</div>
                </div>

                {/* 2 départs garantis par mois */}
                <div className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Ship" className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="text-3xl font-bold text-green-600 mb-2">2</div>
                  <div className="text-gray-700 font-medium">départs garantis par mois</div>
                </div>

                {/* Suivi colis en temps réel */}
                <div className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="MapPin" className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="text-3xl font-bold text-green-600 mb-2">24/7</div>
                  <div className="text-gray-700 font-medium">Suivi colis en temps réel</div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Competitive Pricing Explanation Block */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto bg-gradient-to-br from-gray-50 to-green-50 rounded-2xl shadow-lg p-8 md:p-12 border-t-4 border-green-600"
            >
              <div className="flex items-start mb-6">
                <div className="bg-green-600 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <Icon name="TrendingDown" className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Pourquoi nos tarifs sont compétitifs ?
                </h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                Grâce au groupage maritime optimisé, à nos départs réguliers et à une logistique directe Paris – Abidjan, nous réduisons les coûts tout en garantissant sécurité et traçabilité.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Urgency Block - Next Departure */}
        <section className="py-12 bg-gradient-to-br from-orange-50 to-red-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-8 border-orange-500"
            >
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                {/* Icon */}
                <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="Clock" className="w-10 h-10 text-orange-600" />
                </div>

                {/* Content */}
                <div className="flex-1 text-center md:text-left">
                  <div className="inline-block bg-orange-500 text-white px-4 py-1.5 rounded-full text-sm font-bold mb-4">
                    PROCHAIN DÉPART
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                    Prochain départ maritime : 7 mars 2026
                  </h3>
                  <p className="text-lg text-gray-700 mb-6">
                    Réservez votre expédition avant le chargement du conteneur.
                  </p>
                  <a href="/public-maritime-order-page">
                    <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white font-bold shadow-lg">
                      <Icon name="Calendar" className="mr-2" />
                      Réserver maintenant
                    </Button>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Comparison Table - Improved Design */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Quelle barrique choisir ?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comparaison détaillée pour vous aider à faire le bon choix
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="px-6 py-4 text-left font-bold bg-gray-100 text-gray-900">Caractéristique</th>
                      <th className="px-6 py-4 text-center font-bold bg-green-600 text-white">Barrique noire</th>
                      <th className="px-6 py-4 text-center font-bold bg-blue-600 text-white">Barrique bleue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonFeatures?.map((row, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 text-gray-900 font-medium">{row?.feature}</td>
                        <td className="px-6 py-4 text-center text-gray-700">{row?.noir}</td>
                        <td className="px-6 py-4 text-center text-gray-700">{row?.bleu}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Side-by-side recommendation cards */}
            <div className="max-w-4xl mx-auto mt-8 grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Icon name="ThumbsUp" className="mr-2 text-green-600 w-6 h-6" />
                  Choisissez la barrique noire si :
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Icon name="Check" className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Vous cherchez le tarif le plus économique</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Vous bénéficiez de l'Offre Été 2026 à 160€ TTC tout compris</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Vous envoyez des affaires personnelles standard</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Icon name="Award" className="mr-2 text-blue-600 w-6 h-6" />
                  Choisissez la barrique bleue si :
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Icon name="Check" className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Vous avez besoin de plus de résistance</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Vous envoyez des objets volumineux ou lourds</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Vous voulez optimiser la capacité</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Catalogue Complet - Tabbed System */}
        <section ref={catalogRef} className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Catalogue Complet des Tarifs
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Prix de transport pour tous types d'articles vers Abidjan
              </p>
            </motion.div>

            <div className="max-w-5xl mx-auto">
              {/* Tabs Navigation */}
              <div className="flex flex-wrap gap-2 mb-8 justify-center">
                {Object.keys(pricingCatalog)?.map((key) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 ${
                      activeTab === key
                        ? 'bg-green-600 text-white shadow-lg'
                        : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-green-600'
                    }`}
                  >
                    <Icon name={pricingCatalog?.[key]?.icon} className="w-5 h-5" />
                    {pricingCatalog?.[key]?.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden"
                >
                  {/* Category Header */}
                  <div className="bg-green-600 text-white p-6 flex items-center">
                    <Icon name={pricingCatalog?.[activeTab]?.icon} className="w-10 h-10 mr-4" />
                    <h3 className="text-2xl font-bold">{pricingCatalog?.[activeTab]?.label}</h3>
                  </div>

                  {/* Items Table */}
                  <div className="p-6">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-gray-200">
                          <th className="text-left py-3 px-4 font-bold text-gray-900">Article</th>
                          <th className="text-left py-3 px-4 font-bold text-gray-900">Taille ou détail</th>
                          <th className="text-right py-3 px-4 font-bold text-gray-900">Prix</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pricingCatalog?.[activeTab]?.items?.map((item, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="py-3 px-4 text-gray-900 font-medium">{item?.name}</td>
                            <td className="py-3 px-4 text-gray-600">{item?.dimensions || '-'}</td>
                            <td className="py-3 px-4 text-right text-lg font-bold text-green-600">{item?.price}€</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Exclusion Notice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mt-12 max-w-4xl mx-auto"
            >
              <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-6">
                <div className="flex items-start">
                  <Icon name="AlertCircle" className="w-8 h-8 text-red-600 mr-4 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-red-900 mb-2">EXCLUSION</h3>
                    <p className="text-red-800 font-semibold text-lg">
                      Pas de transport de véhicules
                    </p>
                    <p className="text-red-700 mt-2">
                      Nous n'assurons pas le transport de véhicules (voitures, motos, scooters). Pour tout autre article, n'hésitez pas à nous consulter.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Savings Tips - Harmonized */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Conseils pour optimiser vos coûts d'envoi
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Astuces pour réduire le tarif de votre envoi vers la Côte d'Ivoire
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {savingsTips?.map((tip, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="bg-green-100 w-14 h-14 rounded-full flex items-center justify-center mb-4">
                    <Icon name={tip?.icon} className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{tip?.title}</h3>
                  <p className="text-gray-600 text-sm">{tip?.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Quote Calculator - Enhanced with Gray Background */}
        <section ref={calculatorRef} className="py-16 bg-gray-100">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Calculateur de devis personnalisé
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Obtenez une estimation précise pour votre envoi vers la Côte d'Ivoire
              </p>
            </motion.div>
            <div className="bg-white rounded-xl shadow-lg p-8 max-w-5xl mx-auto">
              <QuoteCalculator />
            </div>
          </div>
        </section>

        {/* FAQ Section - Clean Accordion */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Questions fréquentes sur les tarifs
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Tout ce que vous devez savoir sur nos prix d'envoi vers la Côte d'Ivoire
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto space-y-3">
              {faqPricing?.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full text-left p-6 hover:bg-gray-50 transition-colors duration-200 flex items-center justify-between"
                  >
                    <span className="font-bold text-lg text-gray-900 pr-8">{faq?.question}</span>
                    <Icon 
                      name="ChevronDown" 
                      className={`w-6 h-6 text-green-600 flex-shrink-0 transition-transform duration-300 ${
                        openFaq === index ? 'rotate-180' : ''
                      }`} 
                    />
                  </button>
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 text-gray-700 leading-relaxed">
                          {faq?.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Help Section - Simplified Green Banner */}
        <section className="py-12 bg-green-700">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h3 className="text-2xl font-bold mb-4">
                Une question sur nos tarifs ?
              </h3>
              <p className="text-lg text-green-50 mb-6">
                Appelez-nous au 06 51 98 86 21 ou contactez-nous sur WhatsApp
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:+33651988621">
                  <Button size="lg" className="bg-white !text-green-700 hover:bg-green-50 font-bold">
                    <Icon name="Phone" className="mr-2" />
                    Appeler
                  </Button>
                </a>
                <a href="https://wa.me/33651988621" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-white !text-green-700 hover:bg-green-50 font-bold">
                    <Icon name="MessageCircle" className="mr-2" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Prospectus Download Banner - Clean Design */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white border-2 border-green-600 rounded-xl p-8 text-center">
                <div className="flex items-center justify-center mb-4">
                  <Icon name="FileText" className="w-12 h-12 text-green-600 mr-3" />
                  <h3 className="text-2xl font-bold text-gray-900">
                    Téléchargez notre prospectus complet avec tous nos tarifs
                  </h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Retrouvez l'ensemble de nos prix et services dans un document PDF pratique
                </p>
                <Button 
                  size="lg" 
                  onClick={handleDownloadPDF}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold"
                >
                  <Icon name="Download" className="mr-2" />
                  Télécharger le PDF
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Professional Services CTA Box */}
        <section className="py-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 md:p-10 border-2 border-green-200 shadow-lg"
            >
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="Briefcase" className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                    Vous expédiez régulièrement ou en volume important ?
                  </h3>
                  <p className="text-base md:text-lg text-gray-700 mb-5">
                    Découvrez notre offre dédiée aux professionnels avec des tarifs préférentiels pour le <strong>groupage maritime France Côte d'Ivoire</strong>, 
                    la gestion documentaire complète et un accompagnement personnalisé.
                  </p>
                  <Link to="/professional-services-landing-page">
                    <Button size="lg" className="bg-green-700 hover:bg-green-800 text-white font-bold shadow-md">
                      <Icon name="ArrowRight" className="mr-2" />
                      Découvrir l'offre professionnelle
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 px-4" style={{ background: 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)' }}>
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Prêt à commander ?
              </h2>
              <p className="text-xl text-green-50 mb-8">
                Réservez votre place dans le prochain conteneur vers Abidjan
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
                <a href="https://wa.me/33651988621" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-white !text-green-700 hover:bg-green-50 font-bold px-8 py-4 text-lg">
                    <Icon name="MessageCircle" className="mr-2" />
                    Commander ma barrique maintenant
                  </Button>
                </a>
              </div>
              <div className="text-green-50">
                <p className="mb-2">Ou appelez-nous : <span className="font-bold">06 51 98 86 21</span></p>
                <p>Prochain départ : <span className="font-bold">15 février 2026</span></p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <ContactSection />
          </div>
        </section>
      </div>
    </>
  );
};

export default DedicatedPricingPage;